import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  createStyles,
  Flex,
  Modal,
  Progress,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowRight,
  IconDatabase,
  IconDownload,
  IconFile,
  IconMicrophone,
  IconPaperclip,
  IconRefresh,
  IconSend,
  IconShieldCheck,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  sendSkAiMessage,
  fetchSkAiGreeting,
  getSkAiMemory,
  refreshSkAiMemory,
  upgradeSkAiMemory,
  commitSkAiAction,
  cancelSkAiAction,
  getSkAiAlerts,
} from "../../services/compliance";
import {
  setChatMessages,
  setChatHasGreeted,
  setChatConversationSummary,
} from "../../store/reducers/skaiSlice";
import { skaiDock } from "./skaiDock";

// ─── Quick actions always visible above the input ──────────────────────────────
const QUICK_ACTIONS = [
  { label: "📊 Account overview",   message: "Give me an overview of my account status" },
  { label: "⏰ Expiring soon",       message: "What documents or items are expiring soon?" },
  { label: "👥 Staff credentials",  message: "How are my staff credentials and documents looking?" },
  { label: "✅ Open tasks",          message: "What tasks are currently open or overdue?" },
  { label: "📝 Pending notes",       message: "Are there any notes pending approval?" },
  { label: "📋 Compliance status",   message: "What's my compliance status for state requirements?" },
  { label: "🏥 My clients",          message: "Give me an overview of my clients" },
  { label: "🏢 Facility info",       message: "Tell me about my facility" },
];

// ─── Suggestions shown inline after SkAi messages ─────────────────────────────
const GREETING_SUGGESTIONS = [
  "Which clients have missing care plans?",
  "Any notes pending my approval?",
  "Who needs attention today?",
  "Walk me through my biggest risks",
];

// ─── Styles ────────────────────────────────────────────────────────────────────
const useStyles = createStyles(() => ({
  header: {
    padding: "12px 14px",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    flexShrink: 0,
    borderRadius: "16px 16px 0 0",
  },
  skAiLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#667085",
  },
  skAiTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.2,
  },
  aiBadge: {
    backgroundColor: "var(--app-tint-primary)",
    color: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    fontWeight: 700,
    fontSize: 10,
  },
  messagesArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "14px",
    backgroundColor: "#F9FAFB",
  },
  skAiBubble: {
    backgroundColor: "#FFF",
    border: "1px solid #E5E7EB",
    borderRadius: "4px 12px 12px 12px",
    padding: "10px 12px",
    maxWidth: "92%",
  },
  userBubble: {
    backgroundColor: "#1E293B",
    borderRadius: "12px 12px 4px 12px",
    padding: "9px 12px",
    fontSize: 13,
    lineHeight: 1.6,
    color: "#FFF",
    maxWidth: "85%",
    whiteSpace: "pre-wrap",
  },
  reqCard: {
    border: "1px solid color-mix(in srgb, var(--app-primary) 13%, transparent)",
    borderLeft: "3px solid var(--app-primary)",
    borderRadius: "0 8px 8px 0",
    padding: "7px 10px",
    backgroundColor: "var(--app-tint-primary)",
    marginTop: 6,
    marginLeft: 30,
  },
  diffCard: {
    border: "1px solid #E5E7EB",
    borderLeft: "3px solid #E6A817",
    borderRadius: "0 10px 10px 0",
    padding: "10px 12px",
    backgroundColor: "#FFFDF8",
    marginTop: 8,
    marginLeft: 30,
  },
  reqCategory: {
    backgroundColor: "var(--app-tint-primary)",
    color: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    fontWeight: 700,
    fontSize: 9,
  },
  logBtn: {
    backgroundColor: "transparent",
    color: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 11,
    padding: "2px 8px",
    height: "auto",
    flexShrink: 0,
    "&:hover": { backgroundColor: "var(--app-tint-primary)" },
  },
  suggestionChip: {
    backgroundColor: "#FFF",
    border: "1.5px solid var(--app-primary)",
    borderRadius: 20,
    padding: "4px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: "#111827",
    cursor: "pointer",
    flexShrink: 0,
    userSelect: "none",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: "var(--app-tint-primary)" },
  },
  quickActionChip: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "3px 10px",
    fontSize: 11,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": {
      borderColor: "var(--app-primary)",
      backgroundColor: "var(--app-tint-primary)",
      color: "#111827",
    },
  },
  inputArea: {
    borderTop: "1px solid #E5E7EB",
    padding: "9px 12px 10px",
    backgroundColor: "#FFF",
    flexShrink: 0,
    borderRadius: "0 0 16px 16px",
  },
  quickActionsBar: {
    display: "flex",
    gap: 5,
    flexWrap: "wrap",
    paddingBottom: 7,
  },
  textareaWrap: {
    border: "1.5px solid #E5E7EB",
    borderRadius: 12,
    backgroundColor: "#FAFAFA",
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    transition: "border-color 0.15s",
    "&:focus-within": { borderColor: "var(--app-primary)", backgroundColor: "#FFF" },
  },
  textarea: {
    flex: 1,
    "& textarea": {
      border: "none",
      outline: "none",
      backgroundColor: "transparent",
      fontSize: 13,
      lineHeight: 1.6,
      resize: "none",
      padding: 0,
      color: "#111827",
      "&::placeholder": { color: "#9CA3AF" },
    },
    "& .mantine-Textarea-wrapper": { border: "none !important" },
    "& .mantine-Input-input": {
      border: "none !important",
      boxShadow: "none !important",
      backgroundColor: "transparent !important",
    },
  },
  sendBtn: {
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    borderRadius: 8,
    width: 32,
    height: 32,
    flexShrink: 0,
    "&:hover": { backgroundColor: "color-mix(in srgb, var(--app-primary) 90%, #000)" },
    "&:disabled": { backgroundColor: "#F3F4F6", color: "#9CA3AF" },
  },
}));

// ─── Lightweight Markdown Renderer ────────────────────────────────────────────
const SELECT_RE       = /\[SELECT:\s*([^\]]+)\]/;
const PDF_ARTIFACT_RE = /\[PDF_ARTIFACT:\s*url=([^\s|]+)\s*\|\s*filename=([^\]]+)\]/;

const PdfArtifactCard = ({ url, filename }) => (
  <Box
    style={{
      border: "1.5px solid var(--app-primary)",
      borderRadius: 10,
      padding: "9px 12px",
      backgroundColor: "var(--app-tint-primary)",
      marginTop: 8,
      marginBottom: 4,
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}
  >
    <Box
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: "var(--app-tint-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <IconFile size={16} color="var(--app-primary)" />
    </Box>
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Text style={{ fontSize: 12, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {filename.trim()}
      </Text>
      <Text style={{ fontSize: 11, color: "#6B7280" }}>PDF ready to download</Text>
    </Box>
    <Box
      component="a"
      href={url}
      download
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        backgroundColor: "var(--app-primary)",
        color: "var(--app-on-primary)",
        borderRadius: 7,
        padding: "4px 9px",
        fontSize: 12,
        fontWeight: 600,
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      <IconDownload size={12} />
      Download
    </Box>
  </Box>
);

const InlineText = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} style={{ fontWeight: 700, color: "#111827" }}>{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const SkAiText = ({ text, onSelect }) => {
  const lines = text.split("\n");
  const elements = [];
  let bulletBuffer = [];
  let numBuffer = [];

  const flushBullets = (key) => {
    if (bulletBuffer.length) {
      elements.push(
        <Box key={`ul-${key}`} component="ul" style={{ margin: "5px 0", paddingLeft: 16 }}>
          {bulletBuffer.map((b, i) => (
            <Box key={i} component="li" style={{ fontSize: 13, lineHeight: 1.65, color: "#111827", marginBottom: 2 }}>
              <InlineText text={b} />
            </Box>
          ))}
        </Box>
      );
      bulletBuffer = [];
    }
    if (numBuffer.length) {
      elements.push(
        <Box key={`ol-${key}`} component="ol" style={{ margin: "5px 0", paddingLeft: 16 }}>
          {numBuffer.map((b, i) => (
            <Box key={i} component="li" style={{ fontSize: 13, lineHeight: 1.65, color: "#111827", marginBottom: 2 }}>
              <InlineText text={b} />
            </Box>
          ))}
        </Box>
      );
      numBuffer = [];
    }
  };

  lines.forEach((line, idx) => {
    const pdfMatch    = PDF_ARTIFACT_RE.exec(line);
    const selectMatch = SELECT_RE.exec(line);
    const bullet      = /^[-•*]\s+(.+)/.exec(line);
    const numbered    = /^\d+\.\s+(.+)/.exec(line);
    const heading     = /^#{1,3}\s+(.+)/.exec(line);

    if (pdfMatch) {
      flushBullets(idx);
      elements.push(<PdfArtifactCard key={`pdf-${idx}`} url={pdfMatch[1]} filename={pdfMatch[2]} />);
    } else if (selectMatch) {
      flushBullets(idx);
      const prefix = line.replace(SELECT_RE, "").trim();
      if (prefix) {
        elements.push(
          <Text key={`sel-pre-${idx}`} style={{ fontSize: 13, lineHeight: 1.65, color: "#111827", marginBottom: 4 }}>
            <InlineText text={prefix} />
          </Text>
        );
      }
      const options = selectMatch[1].split("|").map((o) => o.trim()).filter(Boolean);
      elements.push(
        <Box key={`sel-${idx}`} style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, marginBottom: 4 }}>
          {options.map((opt, i) => (
            <Box
              key={i}
              onClick={() => onSelect && onSelect(opt)}
              style={{
                backgroundColor: "#FFF",
                border: "1.5px solid var(--app-primary)",
                borderRadius: 20,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 600,
                color: "#111827",
                cursor: "pointer",
                userSelect: "none",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--app-tint-primary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#FFF"; }}
            >
              {opt}
            </Box>
          ))}
        </Box>
      );
    } else if (bullet) {
      flushBullets(idx);
      bulletBuffer.push(bullet[1]);
    } else if (numbered) {
      flushBullets(idx);
      numBuffer.push(numbered[1]);
    } else if (heading) {
      flushBullets(idx);
      elements.push(
        <Text key={idx} style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginTop: 8, marginBottom: 2, lineHeight: 1.4 }}>
          <InlineText text={heading[1]} />
        </Text>
      );
    } else if (line.trim() === "") {
      flushBullets(idx);
      elements.push(<Box key={idx} style={{ height: 5 }} />);
    } else if (line.trim().endsWith("?")) {
      flushBullets(idx);
      elements.push(
        <Box
          key={idx}
          style={{
            backgroundColor: "var(--app-tint-primary)",
            borderLeft: "3px solid var(--app-primary)",
            borderRadius: "0 8px 8px 0",
            padding: "7px 10px",
            marginTop: 8,
          }}
        >
          <Flex align="center" gap={5} style={{ marginBottom: 3 }}>
            <IconSparkles size={9} color="var(--app-primary)" />
            <Text style={{ fontSize: 10, fontWeight: 700, color: "var(--app-primary)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              SkAi suggests
            </Text>
          </Flex>
          <Text style={{ fontSize: 13, lineHeight: 1.65, color: "#374151", fontWeight: 600 }}>
            <InlineText text={line} />
          </Text>
        </Box>
      );
    } else {
      flushBullets(idx);
      elements.push(
        <Text key={idx} style={{ fontSize: 13, lineHeight: 1.65, color: "#111827", marginBottom: 2 }}>
          <InlineText text={line} />
        </Text>
      );
    }
  });
  flushBullets("end");
  return <Box>{elements}</Box>;
};

// ─── TypingDots ────────────────────────────────────────────────────────────────
const TypingDots = () => (
  <>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "#9CA3AF",
          animation: `skaiDot 1.2s ease-in-out ${i * 0.2}s infinite`,
        }}
      />
    ))}
  </>
);

// ─── SkAiDrawer — self-contained floating chat panel ───────────────────────────
const SkAiDrawer = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Persistent state from Redux
  const {
    chatMessages: messages,
    chatHasGreeted: hasGreeted,
    chatConversationSummary: conversationSummary,
  } = useSelector((state) => state.skai);

  // Local transient state
  const [isOpen, setIsOpen]           = useState(false);
  // Dock/float state shared with the dashboard Organization Health section.
  const [dock, setDock]               = useState(skaiDock.get());
  useEffect(() => {
    setDock(skaiDock.get()); // re-sync in case state changed before we subscribed
    return skaiDock.subscribe(setDock);
  }, []);
  // The floating button only appears once it's been popped out (activated) from
  // the Command Center. While docked it lives only in Organization Health and
  // does not show anywhere else.
  const floating = dock.popped;
  const dragRef = useRef({ moved: false });
  const startFabDrag = (e) => {
    if (!floating || e.button !== 0) return;
    const wrap = e.currentTarget.closest("[data-skai-wrap]");
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    dragRef.current.moved = false;
    const onMove = (ev) => {
      if (!dragRef.current.moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
      dragRef.current.moved = true;
      const x = Math.max(4, Math.min(window.innerWidth - rect.width - 4, ev.clientX - offX));
      const y = Math.max(4, Math.min(window.innerHeight - rect.height - 4, ev.clientY - offY));
      skaiDock.set({ x, y });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [initialising, setInitialising] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver]   = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [memory, setMemory]           = useState(null);  // { percentUsed, status, tier, tierPrices, message }
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);
  const [memoryBusy, setMemoryBusy]   = useState(false);
  const [alerts, setAlerts]           = useState(null);  // { count, criticalCount, alerts: [...] }

  const scrollRef        = useRef(null);
  const textareaRef      = useRef(null);
  const fileInputRef     = useRef(null);
  const recognitionRef   = useRef(null);
  const baseTextRef      = useRef("");   // input value captured before recording starts
  const committedRef     = useRef("");   // finalized speech segments accumulated so far
  const sendOnStopRef    = useRef(false); // true when user clicked mic-to-stop (vs silence auto-stop)

  // Keep a ref to the latest messages so back-to-back setMessages calls within one
  // handler (e.g. append the user message, then the assistant reply after an await)
  // chain correctly instead of both computing from a stale closure value.
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Redux helpers that mirror the useState API used in the old component
  const setMessages = (updater) => {
    const base = messagesRef.current || [];
    const next = typeof updater === "function" ? updater(base) : updater;
    messagesRef.current = next; // update synchronously so the next call sees it
    dispatch(setChatMessages(next));
  };
  const setHasGreeted        = (v) => dispatch(setChatHasGreeted(v));
  const setConversationSummary = (v) => dispatch(setChatConversationSummary(v));

  const lastAssistantIdx = messages.reduce((acc, m, i) => (m.role === "assistant" ? i : acc), -1);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, loading]);

  // Greet once when first opened
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      sendGreeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load memory allocation status whenever the drawer opens
  useEffect(() => {
    if (isOpen) {
      getSkAiMemory().then((m) => m && setMemory(m));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load proactive alerts on mount (so the FAB badge shows) and refresh on open
  useEffect(() => {
    getSkAiAlerts().then((a) => a && setAlerts(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Memory actions ──
  const applyMemory = (m) => {
    if (!m) return;
    setMemory(m);
    if (m.status === "full") setMemoryModalOpen(true);
  };

  const handleRefreshMemory = async () => {
    setMemoryBusy(true);
    const m = await refreshSkAiMemory();
    setMemoryBusy(false);
    if (m) {
      setMemory(m);
      setMemoryModalOpen(false);
      // Fresh memory = fresh conversation: clear the transcript and re-greet
      dispatch(setChatMessages([]));
      setHasGreeted(false);
      setConversationSummary("");
      sendGreeting();
    }
  };

  const handleUpgradeMemory = async (tier) => {
    setMemoryBusy(true);
    const m = await upgradeSkAiMemory(tier);
    setMemoryBusy(false);
    if (m) {
      setMemory(m);
      setMemoryModalOpen(false);
    }
  };

  const sendGreeting = async () => {
    setInitialising(true);
    const result = await fetchSkAiGreeting();
    if (result?.reply) {
      setMessages([{
        role: "assistant",
        content: result.reply,
        requirements: result.surfacedRequirements || [],
        suggestions: GREETING_SUGGESTIONS,
        isGreeting: true,
      }]);
      setHasGreeted(true);
    }
    setInitialising(false);
  };

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if ((!trimmed && attachedFiles.length === 0) || loading || initialising) return;

    const fileNames = attachedFiles.map((f) => f.name);
    const displayContent = [trimmed, ...fileNames.map((n) => `📎 ${n}`)].filter(Boolean).join("\n");

    const userMsg = { role: "user", content: displayContent };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    setLoading(true);

    // History now lives server-side (Conversations API) — args kept for signature compat.
    const result = await sendSkAiMessage(trimmed, [], filesToSend, "");

    if (result?.reply) {
      if (result.summary) setConversationSummary(result.summary);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result.reply,
          requirements: result.surfacedRequirements || [],
          // Staged write (propose → confirm): render a diff card with Confirm/Edit/Cancel
          preview: result.preview || null,
          stagedActionId: result.stagedActionId || null,
          proposalStatus: result.stagedActionId ? "pending" : null,
          // Error fallback: show a one-tap retry that re-sends the same message
          retryable: result.retryable || false,
          retryText: result.retryable ? trimmed : null,
        },
      ]);
    }
    if (result?.memory) applyMemory(result.memory);
    setLoading(false);
  };

  // ── Staged-write actions (Confirm / Cancel / Edit) ──
  const setProposalStatus = (idx, status) =>
    setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, proposalStatus: status } : m)));

  const handleConfirmAction = async (idx, stagedActionId) => {
    setProposalStatus(idx, "committing");
    const res = await commitSkAiAction(stagedActionId);
    if (res && !res.error) {
      setProposalStatus(idx, "committed");
      setMessages((prev) => [...prev, { role: "assistant", content: "✓ Done — the change has been saved." }]);
    } else {
      setProposalStatus(idx, "pending");
    }
  };

  const handleCancelAction = async (idx, stagedActionId) => {
    setProposalStatus(idx, "cancelled");
    await cancelSkAiAction(stagedActionId);
    setMessages((prev) => [...prev, { role: "assistant", content: "Okay, I won't make that change." }]);
  };

  const handleEditAction = (idx) => {
    setProposalStatus(idx, "cancelled");
    setInput("Actually, change that to ");
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setAttachedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const unique = newFiles.filter((f) => !existing.has(f.name));
      return [...prev, ...unique].slice(0, 5);
    });
    e.target.value = "";
  };

  const removeFile = (name) => setAttachedFiles((prev) => prev.filter((f) => f.name !== name));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ─── Drag & Drop ─────────────────────────────────────────────────────────────
  const ALLOWED_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
  ];

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    const valid = dropped.filter((f) => ALLOWED_TYPES.includes(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length === 0) return;
    setAttachedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !existing.has(f.name))].slice(0, 5);
    });
  };

  // ─── Microphone (Web Speech API — real-time interim results) ─────────────────
  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn("[SkAi] SpeechRecognition not supported in this browser");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous      = true;   // keep listening until explicitly stopped
    recognition.interimResults  = true;   // fire events with partial results as user speaks
    recognition.lang            = "en-US";
    recognitionRef.current      = recognition;

    // Snapshot whatever was already in the input box so we can prepend it
    baseTextRef.current    = input;
    committedRef.current   = "";

    recognition.onresult = (event) => {
      let interim   = "";
      let newFinal  = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) newFinal += text;
        else interim += text;
      }

      if (newFinal) committedRef.current += newFinal;

      // Show live preview in input while speaking: base text + committed + interim
      const base = baseTextRef.current;
      const sep  = base && (committedRef.current || interim) ? " " : "";
      setInput(base + sep + committedRef.current + interim);
    };

    recognition.onerror = (e) => {
      console.error("[SkAi] SpeechRecognition error:", e.error);
      setIsRecording(false);
    };

    recognition.onend = () => {
      setIsRecording(false);

      const base       = baseTextRef.current;
      const spoken     = committedRef.current.trim();
      const sep        = base && spoken ? " " : "";
      const finalText  = (base + sep + spoken).trimEnd();

      if (sendOnStopRef.current && finalText) {
        // User clicked the mic button to stop — send immediately, clear the preview
        sendOnStopRef.current = false;
        setInput("");
        send(finalText);
      } else {
        // Auto-stopped (silence timeout) — just leave text in input for user to edit/send
        sendOnStopRef.current = false;
        setInput(finalText);
        setTimeout(() => textareaRef.current?.focus(), 50);
      }
    };

    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      sendOnStopRef.current = true;  // mark as intentional stop → auto-send
      recognitionRef.current.stop(); // triggers onend
    }
  };

  const handleMicClick = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      data-skai-wrap
      style={{
        position: "fixed",
        ...(dock.x != null
          ? { left: dock.x, top: dock.y, bottom: "auto", right: "auto" }
          : { bottom: 24, right: 24 }),
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 12,
        pointerEvents: "none", // let clicks pass through the container itself
      }}
    >
      {/* ── Floating Chat Panel ── */}
      {isOpen && (
        <Box
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            width: 480,
            maxWidth: "calc(100vw - 48px)",
            height: 680,
            maxHeight: "calc(100vh - 110px)",
            borderRadius: 16,
            boxShadow: "0 24px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)",
            backgroundColor: "#FFF",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: isDragOver ? "2px dashed var(--app-primary)" : "1px solid #E5E7EB",
            transition: "border 0.15s",
            position: "relative",
            pointerEvents: "all",
          }}
        >
          {/* Drag overlay */}
          {isDragOver && (
            <Box
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                backgroundColor: "rgba(255,247,237,0.92)",
                borderRadius: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                pointerEvents: "none",
              }}
            >
              <IconFile size={36} color="var(--app-primary)" />
              <Text style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                Drop files here
              </Text>
              <Text style={{ fontSize: 12, color: "#6B7280" }}>
                PDF, DOCX, TXT · max 10 MB each
              </Text>
            </Box>
          )}
          {/* Header */}
          <Box className={classes.header}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={9}>
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconSparkles size={16} color="var(--app-primary)" />
                </Box>
                <Box>
                  <Text className={classes.skAiLabel}>SkAi Beam</Text>
                  <Text className={classes.skAiTitle}>Your Compliance Officer</Text>
                </Box>
              </Flex>
              <Flex align="center" gap={7}>
                <Badge className={classes.aiBadge} size="xs" leftSection={<IconSparkles size={9} />}>
                  AI
                </Badge>
                <Flex align="center" gap={4}>
                  <Box
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: initialising ? "#F59E0B" : "#10B981",
                      flexShrink: 0,
                    }}
                  />
                  <Text size={11} color="dimmed">
                    {initialising ? "Thinking…" : "Online"}
                  </Text>
                </Flex>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={() => setIsOpen(false)}
                  style={{ marginLeft: 2 }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Flex>
            </Flex>
          </Box>

          {/* Memory allocation bar */}
          {memory && memory.limitTokens !== null && (
            <Box
              onClick={() => setMemoryModalOpen(true)}
              style={{
                padding: "6px 14px",
                borderBottom: "1px solid #F1F3F5",
                cursor: "pointer",
                background: memory.status === "full" ? "#FEF2F2" : memory.status === "warn" ? "#FFFBEB" : "#FFF",
              }}
            >
              <Flex justify="space-between" align="center" mb={3}>
                <Flex align="center" gap={5}>
                  <IconDatabase size={11} color={memory.status === "ok" ? "#9CA3AF" : "#E6A817"} />
                  <Text size={10} color="dimmed">SkAi memory</Text>
                </Flex>
                <Text size={10} color={memory.status === "full" ? "red" : "dimmed"}>
                  {memory.percentUsed}% used
                </Text>
              </Flex>
              <Progress
                value={memory.percentUsed}
                size={4}
                radius="xl"
                color={memory.status === "full" ? "red" : memory.status === "warn" ? "yellow" : "var(--app-primary)"}
              />
            </Box>
          )}

          {/* Proactive alerts — highest-priority items, each tap-to-fix */}
          {alerts?.alerts?.length > 0 && (
            <Box style={{ padding: "8px 12px", borderBottom: "1px solid #F1F3F5", background: "#FFFDF8", maxHeight: 168, overflowY: "auto" }}>
              <Flex align="center" gap={5} mb={6}>
                <IconShieldCheck size={12} color="var(--app-primary)" />
                <Text size={10} weight={700} style={{ color: "var(--app-primary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {alerts.count} item{alerts.count !== 1 ? "s" : ""} need attention
                </Text>
              </Flex>
              {alerts.alerts.map((al) => (
                <Flex
                  key={al.id}
                  justify="space-between"
                  align="center"
                  gap={8}
                  style={{
                    padding: "6px 8px",
                    marginBottom: 4,
                    borderRadius: 8,
                    border: "1px solid #F1F3F5",
                    borderLeft: `3px solid ${al.severity === "critical" ? "#EF4444" : "#F59E0B"}`,
                    background: "#FFF",
                  }}
                >
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size={11} weight={600} color="#111827" lineClamp={1}>{al.title}</Text>
                    {al.detail && <Text size={10} color="dimmed">{al.detail}</Text>}
                  </Box>
                  <Button
                    size="xs"
                    compact
                    variant="light"
                    color={al.severity === "critical" ? "red" : "orange"}
                    onClick={() => send(al.action?.prompt || al.title)}
                  >
                    {al.action?.label || "Fix"}
                  </Button>
                </Flex>
              ))}
            </Box>
          )}

          {/* Messages */}
          <Box className={classes.messagesArea} ref={scrollRef}>
            {messages.map((msg, idx) =>
              msg.role === "assistant" ? (
                <Box key={idx} mb={16}>
                  <Flex align="flex-start" gap={7}>
                    <Box
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <IconShieldCheck size={11} color="var(--app-primary)" />
                    </Box>
                    <Box className={classes.skAiBubble}>
                      <SkAiText text={msg.content} onSelect={(opt) => send(opt)} />
                      {msg.retryable && !loading && (
                        <Flex
                          align="center"
                          gap={5}
                          mt={8}
                          style={{ cursor: "pointer", color: "#E6A817", width: "fit-content" }}
                          onClick={() => send(msg.retryText || undefined)}
                        >
                          <IconRefresh size={13} />
                          <Text size={12} weight={600}>Try again</Text>
                        </Flex>
                      )}
                      {msg.suggestions?.length > 0 && idx === lastAssistantIdx && !loading && !initialising && (
                        <>
                          <Box style={{ borderTop: "1px solid #F3F4F6", margin: "8px 0 7px" }} />
                          <Flex gap={5} style={{ flexWrap: "wrap" }}>
                            {msg.suggestions.map((s, si) => (
                              <Box key={si} className={classes.suggestionChip} onClick={() => send(s)}>
                                {s}
                              </Box>
                            ))}
                          </Flex>
                        </>
                      )}
                    </Box>
                  </Flex>

                  {/* Requirement cards */}
                  {msg.requirements?.map((req, ri) => (
                    <Box key={ri} className={classes.reqCard}>
                      <Flex justify="space-between" align="center" gap={8}>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Badge className={classes.reqCategory} size="xs" mb={3}>
                            {req.category}
                          </Badge>
                          <Text size="xs" weight={600} color="#111827" style={{ lineHeight: 1.3 }}>
                            {req.name}
                          </Text>
                          <Text size={11} color="dimmed" mt={1}>
                            Every {req.frequency?.value} {req.frequency?.unit}
                            {req.regulatoryReference ? ` · ${req.regulatoryReference}` : ""}
                          </Text>
                        </Box>
                        <Button
                          className={classes.logBtn}
                          rightIcon={<IconArrowRight size={10} />}
                          onClick={() => navigate("/home/compliance")}
                          compact
                        >
                          Log it now
                        </Button>
                      </Flex>
                    </Box>
                  ))}

                  {/* Staged-write diff card (propose → confirm) */}
                  {msg.preview && (
                    <Box className={classes.diffCard}>
                      <Flex align="center" gap={6} mb={6}>
                        <IconShieldCheck size={13} color="#E6A817" />
                        <Text size="xs" weight={700} color="#111827">
                          {msg.preview.title || "Confirm change"}
                        </Text>
                      </Flex>
                      {(msg.preview.fields || []).map((f, fi) => (
                        <Flex key={fi} gap={6} mb={2}>
                          <Text size={11} color="dimmed" style={{ minWidth: 86 }}>
                            {f.label}
                          </Text>
                          <Text size={11} color="#111827" weight={500} style={{ flex: 1, wordBreak: "break-word" }}>
                            {f.value}
                          </Text>
                        </Flex>
                      ))}

                      {msg.proposalStatus === "pending" && (
                        <Flex gap={6} mt={9}>
                          <Button
                            size="xs"
                            style={{ backgroundColor: "var(--app-primary)", color: "var(--app-on-primary)" }}
                            onClick={() => handleConfirmAction(idx, msg.stagedActionId)}
                          >
                            Confirm
                          </Button>
                          <Button size="xs" variant="default" onClick={() => handleEditAction(idx)}>
                            Edit
                          </Button>
                          <Button size="xs" variant="subtle" color="gray" onClick={() => handleCancelAction(idx, msg.stagedActionId)}>
                            Cancel
                          </Button>
                        </Flex>
                      )}
                      {msg.proposalStatus === "committing" && (
                        <Text size={11} color="dimmed" mt={8}>Saving…</Text>
                      )}
                      {msg.proposalStatus === "committed" && (
                        <Text size={11} color="teal" mt={8} weight={600}>✓ Confirmed</Text>
                      )}
                      {msg.proposalStatus === "cancelled" && (
                        <Text size={11} color="dimmed" mt={8}>Cancelled</Text>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Flex key={idx} justify="flex-end" mb={12}>
                  <Box className={classes.userBubble}>{msg.content}</Box>
                </Flex>
              )
            )}

            {/* Typing indicator */}
            {(initialising || loading) && (
              <Flex align="flex-start" gap={7} mb={14}>
                <Box
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <IconShieldCheck size={11} color="var(--app-primary)" />
                </Box>
                <Box
                  style={{
                    backgroundColor: "#FFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px 12px 12px 12px",
                    padding: "9px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <TypingDots />
                </Box>
              </Flex>
            )}
          </Box>

          {/* Input Area */}
          <Box className={classes.inputArea}>
            <Box className={classes.quickActionsBar}>
              {QUICK_ACTIONS.map((a, i) => (
                <Box key={i} className={classes.quickActionChip} onClick={() => send(a.message)}>
                  {a.label}
                </Box>
              ))}
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,.gif,image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {attachedFiles.length > 0 && (
              <Flex gap={5} mb={5} style={{ flexWrap: "wrap" }}>
                {attachedFiles.map((file) => (
                  <Flex
                    key={file.name}
                    align="center"
                    gap={4}
                    style={{
                      backgroundColor: "var(--app-tint-primary)",
                      border: "1px solid var(--app-primary)",
                      borderRadius: 20,
                      padding: "2px 9px",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#111827",
                      maxWidth: 160,
                    }}
                  >
                    <IconFile size={10} color="var(--app-primary)" style={{ flexShrink: 0 }} />
                    <Text style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                      {file.name}
                    </Text>
                    <Box onClick={() => removeFile(file.name)} style={{ cursor: "pointer", flexShrink: 0, lineHeight: 1, color: "#9CA3AF" }}>
                      <IconX size={10} />
                    </Box>
                  </Flex>
                ))}
              </Flex>
            )}

            <Box className={classes.textareaWrap}>
              <Textarea
                ref={textareaRef}
                className={classes.textarea}
                placeholder="Ask me anything about your account…"
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                minRows={2}
                maxRows={5}
                autosize
                disabled={initialising}
                variant="unstyled"
                styles={{
                  wrapper: { border: "none" },
                  input: { border: "none", padding: 0, fontSize: 13, lineHeight: 1.6, backgroundColor: "transparent", resize: "none" },
                }}
              />
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <ActionIcon
                    size={28}
                    radius={8}
                    variant="default"
                    disabled={loading || initialising}
                    title={isRecording ? "Stop recording" : "Voice input"}
                    onClick={handleMicClick}
                    style={{
                      border: "none",
                      backgroundColor: isRecording ? "#FEE2E2" : "transparent",
                      color: isRecording ? "#EF4444" : "#6B7280",
                      transition: "background-color 0.15s, color 0.15s",
                    }}
                  >
                    <IconMicrophone size={14} style={isRecording ? { animation: "skaiMicPulse 1s ease-in-out infinite" } : {}} />
                  </ActionIcon>
                  <ActionIcon
                    size={28}
                    radius={8}
                    variant="default"
                    disabled={loading || initialising}
                    title="Attach file (PDF, DOCX, TXT — max 10 MB each)"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: "none",
                      backgroundColor: attachedFiles.length > 0 ? "var(--app-tint-primary)" : "transparent",
                      color: attachedFiles.length > 0 ? "var(--app-primary)" : "#9CA3AF",
                    }}
                  >
                    <IconPaperclip size={14} />
                  </ActionIcon>
                </Flex>
                <ActionIcon
                  className={classes.sendBtn}
                  onClick={() => send()}
                  disabled={(!input.trim() && attachedFiles.length === 0) || loading || initialising}
                >
                  <IconSend size={13} />
                </ActionIcon>
              </Flex>
            </Box>

            <Text size={10} color="dimmed" mt={5} align="center">
              SkAi reads your live account data · Shift+Enter for new line
            </Text>
          </Box>
        </Box>
      )}

      {/* ── Floating Action Button (hidden while docked in Organization Health) ── */}
      {floating && (
      <Tooltip label="SkAi Beam — Compliance Officer" position="left" withArrow>
        <Box
          onMouseDown={startFabDrag}
          onClick={() => {
            if (dragRef.current.moved) { dragRef.current.moved = false; return; }
            setIsOpen((o) => !o);
          }}
          style={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            background: isOpen
              ? "linear-gradient(135deg, #374151 0%, #1F2937 100%)"
              : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            cursor: "pointer",
            boxShadow: isOpen
              ? "0 4px 20px rgba(0,0,0,0.25)"
              : "0 4px 20px rgba(0,0,0,0.2), 0 0 0 3px color-mix(in srgb, var(--app-primary) 25%, transparent)",
            transition: "box-shadow 0.2s, background 0.2s, transform 0.15s",
            pointerEvents: "all",
            userSelect: "none",
            position: "relative",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          {/* Proactive-alert badge */}
          {!isOpen && alerts?.count > 0 && (
            <Box
              style={{
                position: "absolute",
                top: -4,
                right: -4,
                minWidth: 20,
                height: 20,
                padding: "0 5px",
                borderRadius: 10,
                background: alerts.criticalCount > 0 ? "#EF4444" : "#F59E0B",
                color: "#FFF",
                fontSize: 11,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #FFF",
                lineHeight: 1,
              }}
            >
              {alerts.count > 9 ? "9+" : alerts.count}
            </Box>
          )}
          {isOpen ? (
            <IconX size={22} color="#FFF" />
          ) : (
            <>
              <IconSparkles size={20} color="var(--app-primary)" />
              <Text
                style={{
                  fontSize: 7,
                  fontWeight: 800,
                  color: "var(--app-primary)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                SkAi
              </Text>
            </>
          )}
        </Box>
      </Tooltip>
      )}

      {/* Memory: refresh / buy-more modal */}
      <Modal
        opened={memoryModalOpen}
        onClose={() => setMemoryModalOpen(false)}
        title={<Text fw={700}>SkAi Memory</Text>}
        centered
        size="sm"
        zIndex={10001}
      >
        <Flex align="center" gap={8} mb={10}>
          <IconDatabase size={18} color="#E6A817" />
          <Text size="sm" fw={600}>
            {memory?.percentUsed ?? 0}% of your memory used
          </Text>
        </Flex>
        <Progress
          value={memory?.percentUsed ?? 0}
          size={6}
          radius="xl"
          color={memory?.status === "full" ? "red" : memory?.status === "warn" ? "yellow" : "var(--app-primary)"}
          mb={12}
        />
        <Text size="sm" color="dimmed" mb={16}>
          {memory?.message ||
            "Your conversation history is how SkAi remembers your clients, staff, and compliance context without you repeating yourself. Add more memory to keep it all, or refresh to start clean."}
        </Text>

        {/* Upgrade options (tiers above current) */}
        {memory?.tierPrices &&
          Object.entries(memory.tierPrices)
            .filter(([key, t]) => key !== "free" && t.price && key !== memory.tier)
            .map(([key, t]) => (
              <Button
                key={key}
                fullWidth
                mb={8}
                variant="light"
                color="orange"
                loading={memoryBusy}
                onClick={() => handleUpgradeMemory(key)}
              >
                {t.label} — {t.memory} · {t.price}
              </Button>
            ))}

        <Button
          fullWidth
          mt={4}
          variant="subtle"
          color="gray"
          leftIcon={<IconRefresh size={14} />}
          loading={memoryBusy}
          onClick={handleRefreshMemory}
        >
          Refresh (clears SkAi's memory)
        </Button>
      </Modal>

      <style>{`
        @keyframes skaiDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes skaiMicPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </Box>
  );
};

export default SkAiDrawer;
